'use client'

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toast } from '@heroui/react'
import { useEffect, useState } from 'react'
import { AuthContext, useAuthProvider } from '@/lib/auth'
import { isIgnorableClientTransitionError } from '@/lib/clientErrors'
import { appToastQueue, toastError, type QueryErrorMeta } from '@/lib/toast'

function metaSuppressesToast(meta: unknown): boolean {
  return Boolean((meta as QueryErrorMeta | undefined)?.suppressErrorToast)
}

export function Providers({ children }: { children: React.ReactNode }): React.ReactElement {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            if (metaSuppressesToast(query.meta)) return
            toastError(error)
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, _variables, _context, mutation) => {
            if (metaSuppressesToast(mutation.meta)) return
            toastError(error)
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
  )
  const auth = useAuthProvider()

  useEffect(() => {
    const onUnhandled = (event: PromiseRejectionEvent) => {
      if (!isIgnorableClientTransitionError(event.reason)) return
      event.preventDefault()
    }
    const onError = (event: ErrorEvent) => {
      if (!isIgnorableClientTransitionError(event.error)) return
      event.preventDefault()
    }
    window.addEventListener('unhandledrejection', onUnhandled)
    window.addEventListener('error', onError)
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandled)
      window.removeEventListener('error', onError)
    }
  }, [])

  return (
    <AuthContext.Provider value={auth}>
      <QueryClientProvider client={queryClient}>
        <Toast.Provider queue={appToastQueue} placement="top end" maxVisibleToasts={4} />
        {children}
      </QueryClientProvider>
    </AuthContext.Provider>
  )
}
